import api from "../api";
import type { User } from "../auth";

export interface Discussion {
  id: string;
  title: string;
  content: string;
  isPrivate: boolean;
  category?: {
    id: string;
    name: string;
    slug: string;
    color?: string;
  };
  tags: Array<{ id: string; name: string; slug: string }>;
  author: User;
  replyCount: number;
  likeCount: number;
  followerCount: number;
  isFollowing: boolean;
  hasLiked: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DiscussionCategory {
  id: string;
  name: string;
  slug: string;
  color?: string;
  discussionCount: number;
}

const discussionService = {
  getDiscussions: async (
    params: {
      page?: number;
      limit?: number;
      category?: string;
      isPrivate?: boolean;
      search?: string;
      sortBy?: "recent" | "popular";
      followed?: boolean;
    } = {},
  ): Promise<{ discussions: Discussion[]; pagination: any }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, value.toString());
    });

    const response = await api.get<{
      discussions: Discussion[];
      pagination: any;
    }>(`discussions?${queryParams.toString()}`);
    return response.data;
  },

  getDiscussion: async (id: string): Promise<Discussion> => {
    const response = await api.get<Discussion>(`discussions/${id}`);
    return response.data;
  },

  createDiscussion: async (data: any): Promise<Discussion> => {
    const response = await api.post<Discussion>("discussions", data);
    return response.data;
  },

  updateDiscussion: async (
    id: string,
    data: {
      title?: string;
      content?: string;
      isPrivate?: boolean;
      categoryId?: string;
    },
  ): Promise<Discussion> => {
    const response = await api.put<Discussion>(`discussions/${id}`, data);
    return response.data;
  },

  deleteDiscussion: async (id: string): Promise<void> => {
    await api.delete(`discussions/${id}`);
  },

  followDiscussion: async (id: string): Promise<{ followerCount: number }> => {
    const response = await api.post<{ followerCount: number }>(
      `discussions/${id}/follow`,
    );
    return response.data;
  },

  unfollowDiscussion: async (
    id: string,
  ): Promise<{ followerCount: number }> => {
    const response = await api.delete<{ followerCount: number }>(
      `discussions/${id}/follow`,
    );
    return response.data;
  },

  likeDiscussion: async (id: string): Promise<{ hasLiked: boolean }> => {
    const response = await api.post<{ hasLiked: boolean }>(
      `discussions/${id}/like`,
    );
    return response.data;
  },

  addReply: async (id: string, content: string): Promise<any> => {
    const response = await api.post(`discussions/${id}/replies`, { content });
    return response.data;
  },

  getCategories: async (): Promise<{ categories: DiscussionCategory[] }> => {
    const response = await api.get<{ categories: DiscussionCategory[] }>(
      "discussions/categories/list",
    );
    return response.data;
  },

  searchDiscussions: async ({
    query,
    page = 1,
    limit = 10,
  }: {
    query: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get<{
      discussions: Discussion[];
      pagination: { total: number };
    }>("discussions", {
      params: {
        search: query,
        page,
        limit,
      },
    });
    return {
      discussions: response.data?.discussions || [],
      total: response.data?.pagination?.total || 0,
    };
  },
};

export default discussionService;
export const discussionsApi = discussionService;
